import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { firstValueFrom, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { Session } from 'src/app/classes/auth/session';
import { OAuthService } from './oauth.service';
import { AuthService } from './auth.service';
import { ApiService } from '../sp-api/api.service';
import { User } from 'src/app/models/user';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { UserNotFoundError } from 'src/app/errors/user-not-found-error';
import { UserBlockedError } from 'src/app/errors/user-blocked-error';
import { UserUnauthorizedError } from 'src/app/errors/user-unauthorized';
import { Router } from '@angular/router';
import { UserWithoutCompany } from 'src/app/errors/user-without-company';
import { ErrorPagesConfig } from 'src/app/config/error-pages.config';
import { environment } from 'src/environments/environment';
import { isFronteggImpersonationToken } from './frontegg-impersonation';

export interface AuthTokenProvider {
  getAccessToken(): Promise<string>;
  logout(): Promise<void>;
  isAuthenticated(): Promise<boolean>;
}

class OAuthTokenProvider implements AuthTokenProvider {
  constructor(private oauthService: OAuthService) {}

  async getAccessToken(): Promise<string> {
    return await this.oauthService.getAccessToken();
  }

  async logout(): Promise<void> {
    await this.oauthService.logout();
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.oauthService.isAuthenticated();
  }
}

class CookieTokenProvider implements AuthTokenProvider {
  constructor(private cookieService: CookieService, private cookieName: string) {}

  async getAccessToken(): Promise<string> {
    return this.cookieService.get(this.cookieName);
  }

  async logout(): Promise<void> {
    this.cookieService.delete(this.cookieName, '/');
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.cookieService.check(this.cookieName);
  }
}

// Dummy token provider for testing
export class DummyTokenProvider implements AuthTokenProvider {
  constructor() {}

  async getAccessToken(): Promise<string> {
    return 'mock-token';
  }

  async logout(): Promise<void> {
    return;
  }

  async isAuthenticated(): Promise<boolean> {
    return true;
  }
}

@Injectable({
  providedIn: 'root'
})
export class SessionProviderService {
  private _sessionChangeListener: Subject<Session> = new Subject();
  private _session?: Session;
  private _impersonationSession?: Session;

  constructor(
    private oauthService: OAuthService,
    private cookieService: CookieService,
    private api: ApiService,
    private router: Router
  ) {}

  async initializeSession(options?: { silent?: boolean }): Promise<Session> {
    if (this.hasImpersonationSession()) {
      if (!this._impersonationSession) {
        await this.initializeImpersonationSession();
        if (this._session) {
          this._session = undefined;
        }
      }
    } else if (!this._session) {
      await this.initializeOAuthSession(options?.silent ?? false);
    }

    if (this.activeSession) {
      this.sessionChangeListener.next(this.activeSession);
    }

    return this.activeSession;
  }

  async refreshSession(): Promise<Session> {
    this._impersonationSession = undefined;
    this._session = undefined;

    if (this.hasImpersonationSession()) {
      await this.initializeImpersonationSession();
    } else {
      await this.initializeOAuthSession();
    }

    return this.activeSession;
  }

  async hasNoAvailableSession(): Promise<boolean> {
    return !this.hasImpersonationSession() && await !this.hasUserSession();
  }

  async logout() {
    if (this._impersonationSession) {
      await this._impersonationSession.logout();
      this._impersonationSession = undefined;
      if (this.hasUserSession()) {
        await this.initializeSession();
      }
    } else if (this._session) {
      await this._session.logout();
      this._session = undefined;
    } else {
      // Unknown user will not have a session but will be authenticated through OAuth
      // So we need to logout from OAuth just in case
      await this.oauthService.logout();
    }

    posthog.reset();
  }

  private async initializeOAuthSession(silent: boolean = false) {
    const oauthTokenProvider = new OAuthTokenProvider(this.oauthService);
    if (await oauthTokenProvider.isAuthenticated()) {
      this._session = await this.createSession(oauthTokenProvider, false);
    } else if (!silent) {
      await this.oauthService.login();
    }
  }

  private async initializeImpersonationSession() {
    const cookieTokenProvider = new CookieTokenProvider(this.cookieService, AuthService.authImpersonationCookie);
    this._impersonationSession = await this.createSession(cookieTokenProvider, true);
  }

  private async createSession(authTokenProvider: AuthTokenProvider, impersonation: boolean): Promise<Session> {
    try {
      const token = await authTokenProvider.getAccessToken();
      const user = await this.getSessionUser(token);
      const session = new Session(authTokenProvider, user, impersonation, isFronteggImpersonationToken(token));
      return session;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          if (error.error.code === 'CAPI_USER_NOT_FOUND') {
            const userNotFoundError = new UserNotFoundError('User not found');
            await this.handleSessionError(userNotFoundError, authTokenProvider);
          } else if (error.error.code === 'CAPI_USER_BLOCKED') {
            const blockedError = new UserBlockedError('User blocked', error.error.message);
            await this.handleSessionError(blockedError, authTokenProvider);
          } else {
            const unauthorizedError = new UserUnauthorizedError('User unauthorized');
            await this.handleSessionError(unauthorizedError, authTokenProvider);
          }
        }
      }

      throw error;
    }
  }

  private async handleSessionError(error: any, authTokenProvider: AuthTokenProvider): Promise<void> {
    if (error instanceof UserUnauthorizedError) {
      await authTokenProvider.logout();
    } else if (error instanceof UserNotFoundError) {
      if (environment.config.production) {
        window.location.href = ErrorPagesConfig.accessDenied;
      } else {
        this.logout();
      }
    } else if (error instanceof UserBlockedError) {
      await this.router.navigate(['page-user-blocked'], { queryParams: { reason: error.blockedReason } });
    } else {
      await this.router.navigate(['page-something-wrong']);
    }
  }

  private async getSessionUser(token: string): Promise<User> {
    const userPromise = firstValueFrom(this.api.get<User>("user", undefined, new HttpHeaders({Authorization: "Bearer " + token})));
    userPromise.then(user => {
      if (!user.company){
        throw new UserWithoutCompany('User company not found', user.oauth_id);
      }
    });
    return userPromise;
  }

  private hasImpersonationSession(): boolean {
    return this.cookieService.check(AuthService.authImpersonationCookie);
  }

  private hasUserSession(): Promise<boolean> {
    return this.oauthService.isAuthenticated();
  }

  get activeSession(): Session { return this._impersonationSession ?? this._session; }
  get sessionChangeListener(): Subject<Session> { return this._sessionChangeListener; }
}
